import type { FieldValues } from 'react-hook-form';

import z from 'zod';

import { registryStore } from '@/stores/registry';

function inferFieldDefault(fieldSchema: z.ZodType): string | boolean | unknown[] {
  const inner =
    fieldSchema instanceof z.ZodOptional || fieldSchema instanceof z.ZodNullable
      ? (fieldSchema.unwrap() as z.ZodType)
      : fieldSchema;

  if (inner instanceof z.ZodBoolean) {
    return false;
  }

  if (inner instanceof z.ZodArray) {
    return [];
  }

  return '';
}

export function extractShape(schema: z.ZodType): Record<string, z.ZodType> | null {
  if (schema instanceof z.ZodObject) {
    return schema.shape;
  }

  if (!(schema instanceof z.ZodUnion || schema instanceof z.ZodDiscriminatedUnion)) {
    return null;
  }

  const shapes = schema.options
    .filter((opt): opt is z.ZodObject<z.ZodRawShape> => opt instanceof z.ZodObject)
    .map(opt => opt.shape);

  if (!shapes.length) {
    return null;
  }

  const merged: Record<string, z.ZodType> = Object.assign({}, ...shapes.reverse());
  return Object.keys(merged).length > 0 ? merged : null;
}

export function defaultValuesForValidator(validator: z.ZodType<FieldValues, FieldValues>): FieldValues {
  const shape = extractShape(validator);

  if (!shape) {
    return {};
  }

  const slice = registryStore.getState().registry as Record<string, unknown>;
  const defaults: FieldValues = {};

  for (const [key, fieldSchema] of Object.entries(shape)) {
    const persisted = slice[key];
    const fallback = inferFieldDefault(fieldSchema);

    if (Array.isArray(fallback)) {
      defaults[key] = Array.isArray(persisted) ? persisted : fallback;
    } else {
      defaults[key] = typeof persisted === typeof fallback ? persisted : fallback;
    }
  }

  return defaults;
}

export const scrollToFirstFieldError = (names: string[], setFocus: (name: string) => void): void => {
  const elements = names
    .map(name => ({
      name,
      element: document.querySelector(
        `[name="${CSS.escape(name)}"], [data-field-anchor="${CSS.escape(name)}"]`
      ) as HTMLElement | null
    }))
    .filter((entry): entry is { name: string; element: HTMLElement } => Boolean(entry.element));

  elements.sort((a, b) => {
    const position = a.element.compareDocumentPosition(b.element);

    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      return -1;
    }

    if (position & Node.DOCUMENT_POSITION_PRECEDING) {
      return 1;
    }

    return 0;
  });

  const first = elements[0];

  if (!first) {
    return;
  }

  requestAnimationFrame(() => {
    first.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFocus(first.name);
  });
};
